import { Injectable, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';

/**
 * Permission Service
 *
 * Manages user permissions with reactive Signal-based state management.
 * Permissions are loaded from JWT tokens and merged with environment-based permissions.
 *
 * Permission checks support:
 * - Single permission: has('asset.create')
 * - OR logic: hasAny(['asset.create', 'asset.update'])
 * - AND logic: hasAll(['asset.create', 'asset.update'])
 * - Feature flags: hasFeature('photos') -> checks feat.photos
 *
 * Super-admins automatically pass all permission checks.
 *
 * Environment-based permissions (like 'feature.artwork-override') are automatically
 * merged into the permission set, allowing them to work with all existing permission
 * infrastructure (hasPermission directive, has(), hasAny(), etc.)
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private logger = inject(LoggerService).getLogger('permissions');

  // Private writable signals
  private permissionsSignal = signal<Map<string, 'allow' | 'deny'>>(new Map());
  private isSuperAdminSignal = signal(false);

  // Public readonly signals - prevents external modification
  permissions = this.permissionsSignal.asReadonly();
  isSuperAdmin = this.isSuperAdminSignal.asReadonly();

  /**
   * Set permissions from JWT payload and merge with environment permissions
   * Converts the permissions object to a Map for O(1) lookup performance
   *
   * Environment permissions are merged AFTER user permissions, meaning:
   * - If environment sets 'feature.x': true, it's added as 'allow'
   * - If environment sets 'feature.x': false, it's NOT added (user permissions take precedence)
   * - User permissions from JWT always take precedence for overlapping keys
   *
   * @param permissions - Record of permission keys to 'allow' or 'deny' from JWT
   * @param isSuperAdmin - Whether the user is a super-admin
   */
  setPermissions(permissions: Record<string, 'allow' | 'deny'>, isSuperAdmin: boolean): void {
    const permissionsMap = new Map<string, 'allow' | 'deny'>();

    // First, add user permissions from JWT
    for (const [key, value] of Object.entries(permissions)) {
      permissionsMap.set(key, value);
    }

    // Then, merge environment-based permissions
    // Only add permissions that are explicitly set to true in the environment
    if (environment.permissions) {
      for (const [key, value] of Object.entries(environment.permissions)) {
        // Only add if:
        // 1. Value is true (enabled in this environment)
        // 2. Not already set by user permissions (user permissions take precedence)
        if (value === true && !permissionsMap.has(key)) {
          permissionsMap.set(key, 'allow');
        }
      }
    }

    this.permissionsSignal.set(permissionsMap);
    this.isSuperAdminSignal.set(isSuperAdmin);

    this.logger.debug('Permissions loaded:', {
      isSuperAdmin,
      permissionCount: permissionsMap.size,
      permissions: Array.from(permissionsMap.entries()),
    });
  }

  /**
   * Clear all permissions (e.g., on logout)
   * Resets to empty state with no permissions and no super-admin status
   */
  clearPermissions(): void {
    this.permissionsSignal.set(new Map());
    this.isSuperAdminSignal.set(false);
  }

  /**
   * Check if user has a specific permission
   * Super-admins automatically return true for all permission checks
   *
   * @param permission - Permission key to check (e.g., 'asset.create')
   * @returns true if user has the permission (allowed), false otherwise
   */
  has(permission: string): boolean {
    // Super-admin bypasses all permission checks
    if (this.isSuperAdminSignal()) {
      return true;
    }

    // Check if permission exists and is 'allow'
    const permissionValue = this.permissionsSignal().get(permission);
    return permissionValue === 'allow';
  }

  /**
   * Check if user has ANY of the specified permissions (OR logic)
   * Returns true if user has at least one allowed permission from the list
   * Super-admins automatically return true
   *
   * @param permissions - Array of permission keys
   * @returns true if user has at least one of the permissions
   */
  hasAny(permissions: string[]): boolean {
    // Super-admin bypasses all permission checks
    if (this.isSuperAdminSignal()) {
      this.logger.debug(`hasAny(${permissions.join(', ')}) = true (super-admin)`);
      return true;
    }

    // Handle empty array
    if (permissions.length === 0) {
      this.logger.debug('hasAny([]) = false (empty array)');
      return false;
    }

    // Check if ANY permission is allowed
    const result = permissions.some((permission) => {
      const permissionValue = this.permissionsSignal().get(permission);
      return permissionValue === 'allow';
    });

    this.logger.debug(`hasAny(${permissions.join(', ')}) = ${result}`, {
      requestedPermissions: permissions,
      foundPermissions: permissions.map((p) => ({
        key: p,
        value: this.permissionsSignal().get(p) || 'not-found',
      })),
    });

    return result;
  }

  /**
   * Check if user has ALL of the specified permissions (AND logic)
   * Returns true only if user has all specified permissions allowed
   * Super-admins automatically return true
   *
   * @param permissions - Array of permission keys
   * @returns true if user has all of the permissions
   */
  hasAll(permissions: string[]): boolean {
    // Super-admin bypasses all permission checks
    if (this.isSuperAdminSignal()) {
      return true;
    }

    // Handle empty array - considered as "has all zero permissions"
    if (permissions.length === 0) {
      return true;
    }

    // Check if ALL permissions are allowed
    return permissions.every((permission) => {
      const permissionValue = this.permissionsSignal().get(permission);
      return permissionValue === 'allow';
    });
  }

  /**
   * Check if user has a specific feature flag enabled
   * This is a convenience method that checks for 'feat.{featureName}' permissions
   *
   * @param featureName - Feature name (without 'feat.' prefix)
   * @returns true if user has the feature permission
   *
   * @example
   * hasFeature('photos') // Checks feat.photos
   */
  hasFeature(featureName: string): boolean {
    return this.has(`feat.${featureName}`);
  }
}
